
import { CodexEntrySchema, CodexFieldType, InputValueType, NovelActTree } from '#/index';
import { getCodexMarksFromJSONContent } from '@/utils';
import { ExecutionContext } from '../types';
export * from './toXml';
export * from '../../../utils/getNovelActTree';
export * from './codex';
export * from './novelScene';

export { getNovelActTree } from '@/utils';

// 从当前用户选择的上下文中获取Codex 条目 
export const getCodexEntryFromCustomInput = (inputs: { type: InputValueType, value: string }[], store: ExecutionContext['globalContext']['generateStore']): CodexEntrySchema[] => {
  const result: CodexEntrySchema[] = []
  const ids: string[] = []
  const { codexEntries, codexEntryGroups } = store.getState();

  const pushResult = (data?: CodexEntrySchema | string) => {
    if (typeof data === 'string' && !ids.includes(data)) {
      ids.push(data)
      result.push(codexEntries.find(entry => entry.id === data) as CodexEntrySchema)
    } else if (typeof data === 'object' && !ids.includes(data.id)) {
      ids.push(data.id)
      result.push(data)
    }
  }
  inputs.forEach(field => {
    if (field.type === 'codexEntries') {
      pushResult(field.value)
    } else if (field.type === 'codexType') {
      codexEntries.forEach(entry => {
        if (entry.attributes.type === field.value) {
          pushResult(entry)
        }
      })
    } else if (field.type === 'codexCategory') {
      codexEntryGroups.find(group => group.category?.id === field.value)?.children?.forEach(entry => pushResult(entry))
    } else if (field.type === 'codexFields') {
      codexEntries.forEach(entry => {
        if (entry.fields?.some(f => f.fieldId === field.value)) {
          pushResult(entry)
        }
      })
    } else if (field.type === 'codexTag') {
      codexEntries.forEach(entry => {
        if (entry.attributes.tags?.includes(field.value)) {
          pushResult(entry)
        }
      })
    }
  })

  return result
}


// 从当前位置获取Codex 条目（用户选择的上下文以及所在位置信息）
// 自动从上下文获取Codex 条目，需要把关联的Codex 条目也获取出来
export const getCodexEntryFromPosition = (context: ExecutionContext): CodexEntrySchema[] => {
  const result: CodexEntrySchema[] = []
  const ids: string[] = []
  const currentPosition = context.globalContext.currentPosition;
  const { codexEntries } = context.globalContext.generateStore.getState();

  const pushResult = (data?: CodexEntrySchema | string) => {
    if ((typeof data === 'string' && ids.includes(data))
      || (typeof data === 'object' && ids.includes(data.id))) return
    const entry = typeof data === 'string' ? codexEntries.find(d => d.id === data) : data
    if (entry) {
      ids.push(entry.id)
      result.push(entry)
      // 获取关联的Codex 条目
      entry.relationships.nestedEntries?.forEach(id => pushResult(id))
    }
  }

  // 当前场景
  if (currentPosition?.scene?.attributes.content) {
    currentPosition.scene.attributes.pov?.characterId && pushResult(currentPosition.scene.attributes.pov.characterId)
    currentPosition.scene.relationships.references.forEach(id => pushResult(id))
    getCodexMarksFromJSONContent(currentPosition.scene.attributes.content).forEach(mark => {
      pushResult(mark.id)
    })
  }

  // 当前消息
  if (currentPosition?.message) {
    getCodexMarksFromJSONContent(currentPosition.message).forEach(mark => {
      pushResult(mark.id)
    })
  }

  // 自定义输入值
  if (currentPosition?.inputs) {
    Object.entries(currentPosition.inputs).forEach(([inputId, fields]) => {
      const inputDetail = currentPosition?.prompt.inputs?.[inputId]
      if (inputDetail?.addToContext) {
        getCodexEntryFromCustomInput(fields, context.globalContext.generateStore).forEach(entry => {
          pushResult(entry)
        })
      }
    })
  }
  return result
}

export const getCodexEntryFields = (context: ExecutionContext, entry: CodexEntrySchema) => {
  const { codexFields, codexEntries } = context.globalContext.generateStore.getState();
  const result: {
    type: CodexFieldType,
    fieldId: string,
    name: string,
    value: string
  }[] = []
  entry.fields?.forEach(field => {
    const detail = codexFields.find(item => item.id === field.fieldId)
    if (detail) {
      let value: string | undefined;
      if (detail.attributes.type === CodexFieldType.Text) {
        value = field.content.textarea
      } else if (detail.attributes.type === CodexFieldType.Line) {
        value = field.content.lineText
      } else if (detail.attributes.type === CodexFieldType.Dropdown) {
        value = field.content.select?.map(d =>
          detail.attributes.settings.select?.options?.find(item => item.id === d)?.label
        ).filter(d => d).join(', ')
      } else if (detail.attributes.type === CodexFieldType.Codex) {
        value = field.content.codex?.map(d =>
          codexEntries.find(item => item.id === d)?.attributes.name
        ).filter(d => d).join(', ')
      }
      if (value) {
        result.push({
          type: detail.attributes.type,
          fieldId: field.fieldId,
          name: detail.attributes.name,
          value: value
        })
      }
    }
  })
  return result
}

export const getTreeDataById = (context: ExecutionContext, id: string): NovelActTree | undefined => {
  let result: NovelActTree | undefined;
  const traverse = (tree: NovelActTree) => {
    if (tree.id === id) {
      result = tree;
      return;
    }
    tree.children?.forEach(child => traverse(child))
  }
  const { novelActsTree } = context.globalContext.generateStore.getState();
  novelActsTree.forEach(tree => traverse(tree))
  return result
}

const escapeXml = (text: string | undefined): string =>
  (text || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');

export const buildAttrs = (attrs: Record<string, string | undefined>): string =>
  Object.entries(attrs)
    .filter(([, value]) => value !== undefined && value !== '')
    .map(([key, value]) => `${key}="${escapeXml(value)}"`)
    .join(' ');